import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, take, tap, takeUntil } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { InfoCellComponent } from '../../../rx-intuitive-patient-header/components/info-cell/info-cell.component';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { CheckmarkEmptyIconComponent } from '@itero/ui-toolkit-angular/icons';
import { RxIntuitiveDoctorLicenseFacade } from '../../rx-intuitive-doctor-license.facade';
import { SelectComponent } from '@shared/components/select/select.component';
import { Doctor } from '@shared/models/doctor';

@Component({
	selector: 'rx-intuitive-doctor-license',
	templateUrl: './rx-intuitive-doctor-license.component.html',
	styleUrls: ['./rx-intuitive-doctor-license.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		AsyncPipe,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		InfoCellComponent,
		TlkButtonComponent,
		CheckmarkEmptyIconComponent,
		SelectComponent
	],
	providers: [RxIntuitiveDoctorLicenseFacade],
	standalone: true
})
export class RxIntuitiveDoctorLicenseComponent extends BaseDestroyableComponent implements OnInit {
	private readonly facade = inject(RxIntuitiveDoctorLicenseFacade);
	private readonly translate = inject(TranslateService);

	doctorInfo$ = this.facade.doctorInfo$;
	isLicenseEditable$ = this.facade.isLicenseEditable$;
	showLicenseReadOnly$ = this.facade.isLicenseEditable$.pipe(map(editable => !editable));
	canReassignScan$ = this.facade.canReassignScan$;
	cannotReassignScan$ = this.facade.canReassignScan$.pipe(map(canReassign => !canReassign));
	doctors$ = this.facade.doctors$;
	defaultSelectedDoctor$ = this.facade.defaultSelectedDoctor$;
	licenseFormControl = new FormControl<string | null>(null, [
		control => Validators.required(control),
		control => Validators.maxLength(36)(control)
	]);
	treatedByFormControl = new FormControl<Doctor | null>(null);
	isLicenseInputFocused = false;

	@ViewChild('licenseInput') licenseInputRef?: ElementRef<HTMLInputElement>;

	getDoctorDisplayName = (doctor: Doctor): string => {
		return [doctor.Name, doctor.Licence].filter(Boolean).join(' | ');
	};

	get showRequiredPlaceholder(): boolean {
		return this.licenseFormControl.invalid && !this.licenseFormControl.hasError('maxlength');
	}

	get licenseInputPlaceholder(): string {
		if (this.showRequiredPlaceholder && !this.licenseFormControl.value?.trim()) {
			return '';
		}

		return this.translate.instant('DoctorSection.AddLicenseNumber');
	}

	get licenseErrorMessage(): string {
		return this.licenseFormControl.hasError('maxlength')
			? this.translate.instant('DoctorSection.LicenseInvalidLength')
			: this.translate.instant('DoctorSection.LicenseIsRequired');
	}

	getTreatedByTitle(doctorInfo: string): string {
		return `${this.translate.instant('IntuitivePatient.TreatedBy')} ${doctorInfo}`;
	}

	ngOnInit(): void {
		this.facade.getDoctorInformation().pipe(take(1), takeUntil(this.componentAlive$)).subscribe();

		this.facade.license$
			.pipe(
				tap(license => this.licenseFormControl.patchValue(license ?? null, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.defaultSelectedDoctor$
			.pipe(
				take(1),
				tap(doctor => this.treatedByFormControl.patchValue(doctor, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.treatedByFormControl.valueChanges
			.pipe(
				tap(doctor => {
					if (doctor) {
						this.facade.updateReassignedDoctor(doctor);
						this.facade.updateLicense({ license: doctor.Licence || '' });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	saveLicense(): void {
		const value = this.licenseFormControl.value?.trim() ?? '';

		this.licenseFormControl.markAllAsTouched();
		if (this.licenseFormControl.invalid) {
			this.licenseInputRef?.nativeElement?.blur();
		}
		if (this.licenseFormControl.valid) {
			this.facade.updateLicense({ license: value });
		}
	}
}
